import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CategoryOperations } from "../module/CategoryOperations";
import { GaleraRollingRestart } from "./GaleraRollingRestart";
import {
  ActionCard,
  CheckList,
  DataList,
  KeyValue,
  StatusPill,
  type CheckItem,
  type DataListColumn,
  type KVRow,
  Field,
} from "@ozpanel/kit";
import {
  Button,
  ConfirmDialog,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatBytes,
  useAuth,
  useToast,
} from "@ozpanel/ui";

/** GET /galera/join/preflight — galera.JoinReport (join.go). */
interface GaleraJoinPreflight {
  node: string;
  checks: { name: string; ok: boolean; blocking: boolean; detail: string }[];
  can_join: boolean;
  refusals?: string[];
}

// MariaDB and Galera, together, because on this platform they are one system.
//
// 21 operations across the two modules were unreachable — every replication fact, every account
// and access question, the whole recovery path. That path is the one this repository has the most
// scar tissue about: an unclean reboot leaves seqno -1, the SURVIVOR is the node to bootstrap
// from, and `pc.bootstrap=YES` is read-only in a degraded state and cannot rescue it. All of that
// was runbook prose and curl. It is a screen now, and the screen says the dangerous parts out
// loud at the moment you are about to do them.

// ── Replication ───────────────────────────────────────────────────────────────────────────

// The wsrep variables worth a human's attention, in the order a human asks them. The endpoint
// returns ~60; showing all of them in a wall of monospace is the same as showing none, because
// the four that decide whether the cluster is working are buried among fifty-six that do not.
const WSREP_PRIMARY = [
  ["wsrep_cluster_status", "Cluster status", "Primary is the only value that accepts writes. Anything else means every write is refused while the node keeps answering reads and reporting itself running."],
  ["wsrep_cluster_size", "Cluster size", "COUNTS the arbitrator. A healthy two-node cluster reads 3, so 'size 2' is a node missing, not a healthy pair."],
  ["wsrep_local_state_comment", "This node's state", "Synced is the working state. Donor/Desynced means it is feeding another node and is deliberately behind."],
  ["wsrep_ready", "Ready", "Whether this node will accept queries at all."],
  ["wsrep_connected", "Connected", "Whether it can see the group. Connected but non-Primary is the partition case."],
  ["wsrep_last_committed", "Last committed", "The position. When choosing a node to bootstrap from, the HIGHEST of these is the one that has everything."],
  ["wsrep_flow_control_paused", "Flow control paused", "The fraction of time this node spent telling the cluster to slow down. Sustained pausing means one slow node is making every node slow."],
  ["wsrep_local_send_queue_avg", "Send queue (avg)", "A persistently non-zero send queue is this node struggling to keep up with replication."],
  ["wsrep_cert_deps_distance", "Parallelism available", "How much of the write stream could be applied in parallel."],
] as const;

function ReplicationTab() {
  const { client } = useAuth();
  const wsrep = useQuery({
    queryKey: ["galera-wsrep"],
    queryFn: () => client.apiFetch<Record<string, unknown>>("/galera/wsrep"),
    refetchInterval: 15000,
  });
  const ssl = useQuery({
    queryKey: ["galera-ssl"],
    queryFn: () =>
      client.apiFetch<{ configured: boolean; running: boolean; peer_verification: boolean; garbd_configured: boolean }>(
        "/galera/ssl",
      ),
    refetchInterval: 60000,
  });

  const d = wsrep.data ?? {};
  const primary: KVRow[] = WSREP_PRIMARY.map(([key, label, hint]) => ({
    key,
    label,
    value: d[key],
    hint,
    render:
      key === "wsrep_cluster_status"
        ? (v) => <StatusPill tone={String(v) === "Primary" ? "ok" : "bad"}>{String(v ?? "unknown")}</StatusPill>
        : key === "wsrep_local_state_comment"
          ? (v) => <StatusPill tone={String(v) === "Synced" ? "ok" : "warn"}>{String(v ?? "unknown")}</StatusPill>
          : undefined,
  }));

  const rest = Object.entries(d)
    .filter(([k]) => !WSREP_PRIMARY.some(([p]) => p === k))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => ({ key: k, value: v }));

  return (
    <div className="space-y-4">
      <Section title="Replication" bodyClassName="p-0">
        <StateView loading={wsrep.isLoading} error={wsrep.error} errorContext="the replication status">
          <KeyValue rows={primary} />
        </StateView>
      </Section>

      <Section title="Replication transport security" bodyClassName="p-0">
        <StateView loading={ssl.isLoading} error={ssl.error} errorContext="replication TLS">
          <KeyValue
            rows={[
              { key: "configured", label: "TLS configured", value: ssl.data?.configured, goodWhen: true },
              {
                key: "running",
                label: "TLS in force",
                value: ssl.data?.running,
                goodWhen: true,
                hint: "Configured and in force are different facts. A config file that names certificates proves nothing about the socket the server is actually using — this is read from the running server.",
              },
              { key: "peer_verification", label: "Peers verified", value: ssl.data?.peer_verification, goodWhen: true },
              {
                key: "garbd_configured",
                label: "Arbitrator uses TLS",
                value: ssl.data?.garbd_configured,
                goodWhen: true,
                hint: "The arbitrator is a cluster member. If it speaks plaintext while the data nodes do not, it cannot join them.",
              },
            ]}
          />
        </StateView>
        <div className="border-t border-line p-4">
          <ActionCard
            title={ssl.data?.configured ? "Turn replication TLS off for the cluster" : "Turn replication TLS on for the cluster"}
            description={
              ssl.data?.configured
                ? "Records the cluster's decision to replicate in the clear and renders it here; every other node renders it on its own next pass (about two minutes). Nothing changes on the wire until each node's MariaDB restarts."
                : "Records the cluster's decision to encrypt replication with each node's cluster certificate, every peer verified against the cluster CA, and renders it here; every other node renders it on its own next pass (about two minutes). It is in force only after each node's MariaDB restarts."
            }
            actionLabel={ssl.data?.configured ? "Turn TLS off" : "Turn TLS on"}
            danger="high"
            unavailable={ssl.data ? undefined : "The current transport state has not been read."}
            confirm={{
              title: ssl.data?.configured ? "Replicate in the clear?" : "Encrypt replication?",
              body: "A TLS node cannot talk to a plain one. Between the first and the last node restarting onto the new setting, a restarted node cannot rejoin the ones that have not — restart them one after another, not hours apart, and watch this page show it in force on each.",
              confirmLabel: ssl.data?.configured ? "Turn off" : "Turn on",
            }}
            run={() =>
              client.apiFetch("/galera/ssl", { method: "POST", body: JSON.stringify({ enabled: !ssl.data?.configured }) })
            }
            invalidates={[["galera-ssl"]]}
          />
        </div>
      </Section>

      <Section title="All wsrep variables" hint={`${rest.length} more`} bodyClassName="p-0">
        <StateView loading={wsrep.isLoading}>
          <KeyValue rows={rest} dense />
        </StateView>
      </Section>
    </div>
  );
}

// ── Recovery ──────────────────────────────────────────────────────────────────────────────

interface Recovery {
  grastate: Record<string, unknown> | null;
  last_committed: number | string | null;
  position_to_compare: string;
  // null = could not be read (reason under unread) — never the empty gcomm:// it would otherwise look like.
  file_cluster_address: string | null;
  mariadb_active: boolean | null;
  ssl_configured: boolean | null;
  unread?: Record<string, string>;
  note?: string;
}

function RecoveryTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["galera-recovery"],
    queryFn: () => client.apiFetch<Recovery>("/galera/recovery"),
    refetchInterval: 30000,
  });
  // The recovery actions here are the most destructive on the page and none of them asked what
  // the cluster is currently doing. Shared query key with the status tab, so it is one request.
  const wsrep = useQuery({
    queryKey: ["galera-wsrep"],
    queryFn: () => client.apiFetch<Record<string, unknown>>("/galera/wsrep"),
    refetchInterval: 15000,
  });
  // The ConfigInspector answers "is it already converged?" and has all along.
  const drift = useQuery({
    queryKey: ["mariadb-config-diff"],
    queryFn: () => client.apiFetch<{ in_sync?: boolean; drifted?: number }>("/mariadb/config/diff"),
    refetchInterval: 30000,
  });

  return (
    <div className="space-y-4">
      <Section title="Recovery position" bodyClassName="p-0">
        <StateView loading={q.isLoading} error={q.error} errorContext="the recovery position">
          <KeyValue
            rows={[
              { key: "mariadb_active", label: "Server running", value: q.data?.mariadb_active, goodWhen: true },
              {
                key: "position_to_compare",
                label: "Position to compare",
                value: q.data?.position_to_compare,
                hint: "THE number. Before bootstrapping a cluster from any node, compare this across every node that might come back — the highest one has writes the others do not, and bootstrapping from a lower one discards them permanently.",
              },
              { key: "last_committed", label: "Last committed", value: q.data?.last_committed },
              {
                key: "file_cluster_address",
                label: "Configured peers",
                value: q.data && q.data.file_cluster_address == null
                  ? `not read: ${q.data.unread?.file_cluster_address ?? ""}`
                  : q.data?.file_cluster_address,
                hint: "An empty gcomm:// means this node will FORM a new cluster rather than join one. Correct for a deliberate bootstrap, and a split-brain everywhere else.",
              },
              { key: "ssl_configured", label: "TLS configured", value: q.data?.ssl_configured },
            ]}
          />
        </StateView>
        {q.data?.note && (
          <div className="border-t border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{q.data.note}</div>
        )}
      </Section>

      {q.data?.grastate && (
        <Section title="grastate.dat" bodyClassName="p-0">
          <KeyValue
            rows={Object.entries(q.data.grastate).map(([k, v]) => ({
              key: k,
              value: v,
              hint:
                k === "safe_to_bootstrap"
                  ? "Set to 1 only on the node you have PROVEN is the most advanced. A clean shutdown sets it; an unclean reboot leaves 0 and seqno -1, which is why a rebooted node is never the one to bootstrap from."
                  : undefined,
            }))}
          />
        </Section>
      )}

      <Section title="Actions">
        <div className="space-y-3">
          <ActionCard
            title="Recover the position"
            description="Reads this node's committed position from its storage without starting replication. The measurement you take on every survivor before deciding which one to bootstrap."
            actionLabel="Recover position"
            run={() => client.apiFetch("/galera/recover-position", { method: "POST" })}
            invalidates={[["galera-recovery"]]}
          />
          <ActionCard
            title="Promote this node to Primary"
            description="For a node stuck non-Primary after its peers were lost: stops MariaDB cleanly so grastate records a real position, marks it safe to bootstrap and founds a new Primary component from it. A live gcomm:// change cannot do this once quorum is lost. Afterwards this node accepts writes, and every other node that rejoins takes its state FROM here."
            actionLabel="Promote"
            danger="high"
            unavailable={
              String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary" && Number(wsrep.data?.wsrep_cluster_size ?? 0) > 1
                ? `This node is already in a Primary component of ${String(wsrep.data?.wsrep_cluster_size)} members — promoting would create a second one (split brain), and the server refuses it.`
                : undefined
            }
            preflight={[
              {
                ok: q.data?.position_to_compare != null && q.data.position_to_compare !== "",
                label: "Compare the recovery position above with every node that may return",
                detail: `this node: ${String(q.data?.position_to_compare ?? "not read")} — a peer with a higher position holds writes this node does not, and they are discarded when it rejoins`,
              },
            ]}
            confirm={{
              title: "Promote THIS node to Primary?",
              body: "MariaDB on this node restarts. Any write another node accepted that this one does not have is lost permanently when that node rejoins. Only promote the most advanced survivor.",
              confirmLabel: "Promote this node",
            }}
            run={() => client.apiFetch("/galera/promote", { method: "POST" })}
            invalidates={[["galera-wsrep"], ["galera-recovery"]]}
          />
          <ActionCard
            title="Bootstrap a new cluster from this node"
            description="Makes this node the origin of a new primary component. Everything it has becomes the cluster's truth, and everything another node had that this one does not is discarded when that node rejoins."
            actionLabel="Bootstrap"
            danger="high"
            // PREFLIGHT, not a veto. A node reading itself as Primary is usually right, and
            // bootstrapping then really does split the cluster — but "Primary" is this node's own
            // view of a group it may have been cut off from, and the operator recovering a dead
            // cluster is the one person who can tell those apart. Show what was read; let them act.
            preflight={[
              {
                ok: String(wsrep.data?.wsrep_cluster_status ?? "") !== "Primary",
                label:
                  String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary"
                    ? "This node already reports a Primary component"
                    : "This node is not in a Primary component",
                detail:
                  String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary"
                    ? `size ${String(wsrep.data?.wsrep_cluster_size ?? "?")} — bootstrapping would make a second one, and the two groups would certify different writes`
                    : "there is no working group to split",
              },
            ]}
            confirm={{
              title: "Bootstrap the cluster from THIS node?",
              body: "Compare the recovery position on every node that might return first. If any of them is more advanced, its writes are lost permanently — this is not reversible by restarting them in a different order.",
              confirmLabel: "Bootstrap from this node",
            }}
            run={() => client.apiFetch("/galera/bootstrap", { method: "POST" })}
            invalidates={[["galera-wsrep"], ["galera-recovery"]]}
          />
          <ActionCard
            title="Join the existing cluster"
            preflight={[
              {
                ok: String(wsrep.data?.wsrep_cluster_status ?? "") !== "Primary",
                label:
                  String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary"
                    ? "This node already reports itself in a Primary component"
                    : "This node is not currently in a Primary component",
                detail: `wsrep says size ${String(wsrep.data?.wsrep_cluster_size ?? "?")}`,
              },
            ]}
            description="Starts replication against the configured peers, taking a state transfer if this node is too far behind to catch up incrementally."
            actionLabel="Join"
            danger="medium"
            confirm={{
              title: "Join this node to the cluster?",
              body: "If the node is far behind, a full state transfer runs and the donor is desynced while it feeds this one.",
            }}
            run={() => client.apiFetch("/galera/join", { method: "POST" })}
            invalidates={[["galera-wsrep"]]}
          />
          <ActionCard
            title="Check whether a join would work"
            description="Tests reachability, addresses and configuration without starting anything. The cheap answer before the expensive operation."
            actionLabel="Preflight"
            run={() => client.apiFetch<GaleraJoinPreflight>("/galera/join/preflight")}
          />
          <ActionCard
            title="Desync this node"
            unavailable={
              String(wsrep.data?.wsrep_local_state_comment ?? "") === "Desynced"
                ? "This node is already Desynced."
                : undefined
            }
            description="Takes the node out of flow control so it stops holding the cluster back — used before a long local operation. It keeps replicating but no longer makes the others wait for it."
            actionLabel="Desync"
            danger="medium"
            confirm={{
              title: "Desync this node?",
              body: "It stops participating in flow control. Its own writes may fall behind, and it must be resynced before it can be relied on again.",
            }}
            run={() => client.apiFetch("/galera/desync", { method: "POST" })}
            invalidates={[["galera-wsrep"]]}
          />
          <ActionCard
            title="Reconcile the configuration"
            unavailable={
              drift.data?.in_sync === true
                ? "The running configuration already matches what this node intends — there is no drift to reconcile."
                : undefined
            }
            description="Re-derives this node's Galera configuration from the registry and applies it. The repair for a config that drifted — including the file-mode error that silently disables the provider."
            actionLabel="Reconcile"
            danger="medium"
            confirm={{
              title: "Reconcile Galera configuration?",
              body: "Configuration files are rewritten from the registry. A restart may be required for some values to take effect.",
            }}
            run={() => client.apiFetch("/galera/reconcile", { method: "POST" })}
            invalidates={[["galera-wsrep"], ["galera-recovery"]]}
          />
          <ActionCard
            title="Update this node's replication address"
            unavailable={
              // Rewriting the address a cluster is CURRENTLY formed on is how a working member
              // stops being able to rejoin. Correct while it is out of the cluster, dangerous
              // while it is in one.
              String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary" &&
              String(wsrep.data?.wsrep_local_state_comment ?? "") === "Synced"
                ? "This node is Synced in a Primary component — it is replicating on its current address. Changing that address now is how a working member loses its way back in; do it while the node is out of the cluster."
                : undefined
            }
            description="Rewrites the address other nodes are told to reach this one on. Needed after a change of IP, and after a floating address moves."
            actionLabel="Update address"
            danger="medium"
            confirm={{
              title: "Update the replication address?",
              body: "Peers learn the new address on their next reconnect. One that names an address this node does not hold makes it unreachable for replication while everything else keeps working.",
            }}
            run={() => client.apiFetch("/galera/update-address", { method: "POST" })}
            invalidates={[["galera-recovery"]]}
          />
        </div>
      </Section>

      <GarbdConfig />
    </div>
  );
}

function GarbdConfig() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["galera-garbd-raw"],
    queryFn: () => client.apiFetch<{ files: { path?: string; content: string }[] | null }>("/galera/garbd/raw"),
  });
  return (
    <Section title="Arbitrator configuration" bodyClassName="p-0">
      <StateView loading={q.isLoading} error={q.error} errorContext="the arbitrator configuration">
        {(q.data?.files ?? []).map((f, i) => (
          <div key={i} className="border-b border-line last:border-b-0">
            {f.path && <div className="bg-bg2 px-4 py-1.5 font-mono text-[11.5px] text-muted">{f.path}</div>}
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap px-4 py-2 font-mono text-[11.5px]">{f.content}</pre>
          </div>
        ))}
        {(q.data?.files?.length ?? 0) === 0 && (
          <div className="px-4 py-6 text-center text-[13px] text-muted">No arbitrator configuration on this node.</div>
        )}
      </StateView>
    </Section>
  );
}

// ── Databases ─────────────────────────────────────────────────────────────────────────────

interface Database {
  name: string;
  charset: string;
  collation: string;
  size_bytes: number;
  tables: number;
  system: boolean;
  platform?: boolean;
  owner?: string; // for a platform schema: the module that owns it
  purpose?: string;
}

function DatabasesTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["mariadb-databases"],
    queryFn: () =>
      // `orphans` is a COUNT — admin.go increments an int for databases with no owning tenant.
      // It was declared as the list of their names, which is the same count-typed-as-a-collection
      // mistake this console made five times over.
      client.apiFetch<{ items: Database[] | null; orphans: number; size_bytes: number; total: number }>(
        "/mariadb/databases",
      ),
  });
  const verify = useQuery({
    queryKey: ["mariadb-verify"],
    queryFn: () =>
      client.apiFetch<{
        consistent: boolean;
        databases_on_server: number;
        missing_databases: string[] | null;
        missing_users: string[] | null;
        orphan_databases: string[] | null;
        detail?: string;
      }>("/mariadb/verify"),
  });

  const qcDb = useQueryClient();
  const { notify: notifyDb } = useToast();
  // A database with no user can hold data and cannot be connected to. Creating one is the other
  // half of creating a database, and it had no UI.
  const [addUser, setAddUser] = React.useState<{ db: string; username: string; password: string } | null>(null);
  // Creating the DATABASE itself had no UI either — POST /mariadb/databases was reached by no
  // console, so the admin could list databases, add a user to one, and never make one.
  const [newDb, setNewDb] = React.useState<{ name: string; user: string; password: string } | null>(null);

  const columns: DataListColumn<Database>[] = [
    {
      key: "name",
      label: "Database",
      sortable: true,
      render: (d) => (
        <span className="flex items-center gap-2">
          <span className="font-mono text-[12.5px]">{d.name}</span>
          {d.system && <StatusPill tone="neutral">system</StatusPill>}
          {d.platform && <StatusPill tone="accent">{d.owner || "platform"}</StatusPill>}
          {d.platform && d.purpose && <span className="text-[11.5px] text-muted">{d.purpose}</span>}
        </span>
      ),
    },
    { key: "tables", label: "Tables", sortable: true, value: (d) => d.tables },
    { key: "size_bytes", label: "Size", sortable: true, value: (d) => d.size_bytes, render: (d) => formatBytes(d.size_bytes) },
    { key: "collation", label: "Collation", sortable: true, render: (d) => <span className="font-mono text-[11.5px]">{d.collation}</span> },
  ];

  // Consistency is stated as checks rather than a boolean, because "inconsistent" without saying
  // WHICH database or user is missing gives an operator nothing to act on.
  const checks: CheckItem[] = [];
  if (verify.data) {
    checks.push({
      key: "consistent",
      label: "Server matches the platform's records",
      state: verify.data.consistent ? "ok" : "fail",
      detail: verify.data.detail || `${verify.data.databases_on_server} database(s) on this server`,
    });
    for (const n of verify.data.missing_databases ?? []) {
      checks.push({
        key: `missing-db-${n}`,
        label: `Database ${n} is recorded but absent`,
        state: "fail",
        because: "Something expects it to exist. Whatever uses it is failing, or will on its next write.",
      });
    }
    for (const n of verify.data.missing_users ?? []) {
      checks.push({
        key: `missing-user-${n}`,
        label: `Account ${n} is recorded but absent`,
        state: "fail",
        because: "A service holding this credential cannot authenticate. Galera replicates account changes, so an account missing here is usually missing cluster-wide.",
      });
    }
    for (const n of verify.data.orphan_databases ?? []) {
      checks.push({
        key: `orphan-${n}`,
        label: `Database ${n} exists but nothing records it`,
        state: "unknown",
        because: "Not necessarily wrong — it may predate the panel or belong to a support service — but nothing here knows who owns it or whether it is backed up.",
      });
    }
  }

  return (
    <div className="space-y-4">
      <Section title="Consistency" bodyClassName="p-0">
        <StateView loading={verify.isLoading} error={verify.error} errorContext="the consistency check">
          <CheckList items={checks} emptyMessage="Nothing checked." />
        </StateView>
      </Section>

      <Section
        title="Databases"
        hint={q.data ? `${q.data.total} · ${formatBytes(q.data.size_bytes)}` : undefined}
        actions={
          <Button size="sm" variant="accent" onClick={() => setNewDb({ name: "", user: "", password: "" })}>
            New database
          </Button>
        }
        bodyClassName="p-0"
      >
        <div className="p-4">
          <DataList
            items={q.data?.items ?? []}
            columns={columns}
            getRowKey={(d) => d.name}
            loading={q.isLoading}
            error={q.error}
            errorContext="the database list"
            searchable
            searchPlaceholder="Search databases…"
            emptyMessage="No databases."
            rowActions={(d) => (
              <Button size="sm" variant="ghost" onClick={() => setAddUser({ db: d.name, username: "", password: "" })}>
                Add user
              </Button>
            )}
          />
        </div>
      </Section>

      <ConfirmDialog
        open={!!newDb}
        onOpenChange={(o) => !o && setNewDb(null)}
        title="Create a database"
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            <Field mono label="Name" value={newDb?.name ?? ""} placeholder="app_production"
              onChange={(v) => setNewDb(newDb ? { ...newDb, name: v } : null)} />
            <Field mono label="User (optional)" value={newDb?.user ?? ""} placeholder="app"
              onChange={(v) => setNewDb(newDb ? { ...newDb, user: v } : null)} />
            <Field mono type="password" label="Password for that user" value={newDb?.password ?? ""}
              onChange={(v) => setNewDb(newDb ? { ...newDb, password: v } : null)} />
            <p className="text-[11.5px] leading-relaxed text-muted">
              A database with no user can hold data and cannot be connected to. Leaving the user
              blank creates the database alone, which is what you want when an existing account
              will be granted access to it.
            </p>
          </div>
        }
        onConfirm={async () => {
          const d = newDb;
          setNewDb(null);
          if (!d?.name.trim()) return;
          try {
            await client.apiFetch("/mariadb/databases", {
              method: "POST",
              body: JSON.stringify({
                name: d.name.trim(),
                ...(d.user.trim() ? { user: d.user.trim(), password: d.password } : {}),
              }),
            });
            notifyDb({ kind: "success", title: `${d.name} created` });
            void qcDb.invalidateQueries({ queryKey: ["mariadb-databases"] });
            void qcDb.invalidateQueries({ queryKey: ["mariadb-verify"] });
          } catch (e) {
            notifyDb({ kind: "error", title: "Could not create it", body: e instanceof Error ? e.message : String(e) });
          }
        }}
      />

      <ConfirmDialog
        open={!!addUser}
        onOpenChange={(o) => !o && setAddUser(null)}
        title={`Add a user to ${addUser?.db ?? ""}`}
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            <label className="block text-[12px] text-muted">
              Username
              <input value={addUser?.username ?? ""}
                onChange={(e) => setAddUser(addUser ? { ...addUser, username: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            <label className="block text-[12px] text-muted">
              Password
              <input type="password" value={addUser?.password ?? ""}
                onChange={(e) => setAddUser(addUser ? { ...addUser, password: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 font-mono text-[12.5px] text-fg" />
            </label>
            {/* Galera replicates DCL, so this account exists on every node the moment it is
                created here — and a second node creating the same name would overwrite it. */}
            <p className="text-[11.5px] leading-relaxed text-muted">
              The grant is replicated to every node in the cluster, so this user exists everywhere
              as soon as it is created here.
            </p>
          </div>
        }
        onConfirm={() => {
          const a = addUser;
          setAddUser(null);
          if (!a?.username.trim()) return;
          void client
            .apiFetch(`/mariadb/databases/${encodeURIComponent(a.db)}/users`, {
              method: "POST",
              body: JSON.stringify({ username: a.username.trim(), password: a.password }),
            })
            .then(() => {
              notifyDb({ kind: "success", title: "User created" });
              void qcDb.invalidateQueries({ queryKey: ["mariadb-databases"] });
            })
            .catch((e: unknown) => notifyDb({ kind: "error", title: "Could not create it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
    </div>
  );
}

// ── Access ────────────────────────────────────────────────────────────────────────────────

interface Account {
  account: string;
  host: string;
  reach: string;
  allowed: boolean;
  protected?: boolean;
  protected_why?: string;
}

function AccessTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["mariadb-access"],
    queryFn: () =>
      client.apiFetch<{
        accounts: Account[] | null;
        admin_sources: string[] | null;
        peers: string[] | null;
        bind_address: string;
        bind_note?: string;
        note?: string;
      }>("/mariadb/access"),
  });
  const shared = useQuery({
    queryKey: ["mariadb-accounts-shared"],
    queryFn: () =>
      client.apiFetch<{ accounts: string[] | null; node_uuid: string; note?: string }>("/mariadb/accounts/shared"),
  });
  const procs = useQuery({
    queryKey: ["mariadb-processes"],
    queryFn: () =>
      client.apiFetch<{
        items: { id: number; user: string; host: string; db: string; command: string; seconds: number; state: string; query: string }[] | null;
        total: number;
      }>("/mariadb/processes"),
    refetchInterval: 10000,
  });

  const qc = useQueryClient();
  const { notify } = useToast();
  const [killing, setKilling] = React.useState<number | null>(null);
  const [dropping, setDropping] = React.useState<Account | null>(null);

  const act = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        void qc.invalidateQueries({ queryKey: ["mariadb-processes"] });
        void qc.invalidateQueries({ queryKey: ["mariadb-access"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const acctCols: DataListColumn<Account>[] = [
    { key: "account", label: "Account", sortable: true, render: (a) => <span className="font-mono text-[12px]">{a.account}</span> },
    {
      key: "reach",
      label: "Reachable from",
      sortable: true,
      // The interlock: an account's existence says nothing without who can reach it.
      render: (a) => <StatusPill tone={a.reach === "this node only" ? "ok" : "warn"}>{a.reach}</StatusPill>,
    },
    {
      key: "protected",
      label: "Protected",
      render: (a) =>
        a.protected ? (
          <span title={a.protected_why}>
            <StatusPill tone="neutral">yes</StatusPill>
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ];

  const procCols: DataListColumn<{ id: number; user: string; host: string; db: string; command: string; seconds: number; state: string; query: string }>[] = [
    { key: "id", label: "Id", sortable: true, value: (p) => p.id },
    { key: "user", label: "User", sortable: true },
    { key: "db", label: "Database", sortable: true, render: (p) => p.db || <span className="text-muted">—</span> },
    { key: "command", label: "Command", sortable: true },
    { key: "seconds", label: "Age", sortable: true, value: (p) => p.seconds, render: (p) => `${p.seconds}s` },
    { key: "state", label: "State", render: (p) => <span className="text-[12px] text-muted">{p.state}</span> },
  ];

  return (
    <div className="space-y-4">
      <Section title="Who can reach this server" bodyClassName="p-0">
        <StateView loading={q.isLoading} error={q.error} errorContext="database access">
          <KeyValue
            rows={[
              {
                key: "bind_address",
                label: "Bind address",
                value: q.data?.bind_address,
                hint: q.data?.bind_note ?? "What the server listens on. A bind wider than the mesh exposes replication to whatever else can route to it.",
              },
              { key: "admin_sources", label: "Admin networks", value: q.data?.admin_sources ?? [] },
              { key: "peers", label: "Cluster peers", value: q.data?.peers ?? [] },
            ]}
          />
        </StateView>
      </Section>

      <Section title="Accounts" hint={q.data ? `${q.data.accounts?.length ?? 0}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={q.data?.accounts ?? []}
            columns={acctCols}
            getRowKey={(a) => a.account}
            loading={q.isLoading}
            error={q.error}
            errorContext="the account list"
            searchable
            searchPlaceholder="Search accounts…"
            emptyMessage="No accounts."
            rowActions={(a) =>
              a.protected ? null : (
                <Button size="sm" variant="danger" onClick={() => setDropping(a)}>Drop</Button>
              )
            }
          />
        </div>
      </Section>

      <Section title="Shared accounts" bodyClassName="p-0">
        <StateView loading={shared.isLoading} error={shared.error} errorContext="shared accounts">
          <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
            {shared.data?.note ??
              "An account used by more than one node is a cluster bug: Galera replicates account changes, so the second node to provision silently changes the first node's credential. Per-node accounts exist to end exactly that."}
          </div>
          {(shared.data?.accounts?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pb-3">
              {shared.data?.accounts?.map((a) => (
                <span key={a} className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]">{a}</span>
              ))}
            </div>
          )}
        </StateView>
      </Section>

      <ConfirmDialog
        open={killing != null}
        onOpenChange={(o) => !o && setKilling(null)}
        title={`Kill connection ${killing ?? ""}?`}
        confirmLabel="Kill it"
        destructive
        body={
          <p>
            The connection is closed and whatever it was running is rolled back. Unlike Postgres,
            MariaDB has no cancel-the-query-and-keep-the-connection — the client sees a lost
            connection and has to reconnect.
          </p>
        }
        onConfirm={() => {
          const id = killing;
          setKilling(null);
          if (id != null) void act("Connection killed", client.apiFetch(`/mariadb/processes/${encodeURIComponent(String(id))}/kill`, { method: "POST" }));
        }}
      />

      <ConfirmDialog
        open={!!dropping}
        onOpenChange={(o) => !o && setDropping(null)}
        title={`Drop ${dropping?.account ?? ""}?`}
        confirmLabel="Drop it"
        destructive
        body={
          <p>
            The account is removed and anything signing in with it stops working immediately. Galera
            replicates the change to every node, so this is a cluster-wide removal even though it is
            run here.
          </p>
        }
        onConfirm={() => {
          const a = dropping;
          setDropping(null);
          if (a) {
            void act("Account dropped", client.apiFetch(
              `/mariadb/accounts/${encodeURIComponent(a.account)}?host=${encodeURIComponent(a.host)}`,
              { method: "DELETE" },
            ));
          }
        }}
      />

      <Section title="Current connections" hint={procs.data ? `${procs.data.total}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={procs.data?.items ?? []}
            columns={procCols}
            getRowKey={(p) => String(p.id)}
            loading={procs.isLoading}
            error={procs.error}
            errorContext="the process list"
            searchable
            searchPlaceholder="Search connections…"
            emptyMessage="No connections."
            rowActions={(p) => (
              <Button size="sm" variant="danger" onClick={() => setKilling(p.id)}>Kill</Button>
            )}
          />
        </div>
      </Section>

      <Section title="Actions">
        <ActionCard
          title="Give this node its own panel credential"
          description="Replaces a shared panel account with one named for this node. Galera replicates account changes, so a shared name means the second node to provision silently changes the first node's password — this is the fix for that class of fault."
          actionLabel="Make per-node"
          danger="medium"
          confirm={{
            title: "Move this node to a per-node panel credential?",
            body: "A new account named for this node is created and the panel switched to it. The shared account is left in place; remove it once every node has its own.",
          }}
          run={() => client.apiFetch("/mariadb/panel-credential/per-node", { method: "POST" })}
          invalidates={[["mariadb-access"], ["mariadb-accounts-shared"]]}
        />
      </Section>
    </div>
  );
}

// ── Cluster control (mariadb module's own view of the same system) ─────────────────────────

function ClusterControlTab() {
  const { client } = useAuth();
  const wsrep = useQuery({
    queryKey: ["mariadb-cluster-wsrep"],
    queryFn: () => client.apiFetch<Record<string, unknown>>("/mariadb/cluster/wsrep"),
    refetchInterval: 15000,
  });

  return (
    <div className="space-y-4">
      <Section title="Cluster membership" bodyClassName="p-0">
        <StateView loading={wsrep.isLoading} error={wsrep.error} errorContext="cluster membership">
          <KeyValue
            rows={Object.entries(wsrep.data ?? {})
              .filter(([, v]) => v === null || typeof v !== "object")
              .map(([k, v]) => ({ key: k, value: v }))}
            dense
          />
        </StateView>
      </Section>

      <Section title="Actions">
        <div className="space-y-3">
          <ActionCard
            title="Promote this node"
            preflight={[
              {
                ok: !(
                  String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary" &&
                  String(wsrep.data?.wsrep_local_state_comment ?? "") === "Synced"
                ),
                label:
                  String(wsrep.data?.wsrep_cluster_status ?? "") === "Primary" &&
                  String(wsrep.data?.wsrep_local_state_comment ?? "") === "Synced"
                    ? "This node is already Synced in a Primary component"
                    : "This node is not accepting writes",
                detail: `status ${String(wsrep.data?.wsrep_cluster_status ?? "?")}, state ${String(wsrep.data?.wsrep_local_state_comment ?? "?")}`,
              },
            ]}
            description="Makes this node the primary component when the cluster has lost quorum. The same decision as a Galera bootstrap, taken through the database module."
            actionLabel="Promote"
            danger="high"
            confirm={{
              title: "Promote this node to primary?",
              body: "Only if it is the most advanced survivor. Compare the recovery position on every node that might return — a promotion from behind discards the writes the other node had.",
              confirmLabel: "Promote",
            }}
            run={() => client.apiFetch("/mariadb/cluster/promote", { method: "POST" })}
            invalidates={[["mariadb-cluster-wsrep"], ["galera-wsrep"]]}
          />
          <ActionCard
            title="Desync from flow control"
            unavailable={
              String(wsrep.data?.wsrep_local_state_comment ?? "") === "Desynced"
                ? "This node is already Desynced — it is not participating in flow control, so there is nothing to detach it from."
                : undefined
            }
            description="Stops this node holding the cluster back during a long local operation. It keeps replicating; it simply stops making the others wait."
            actionLabel="Desync"
            danger="medium"
            confirm={{
              title: "Desync this node?",
              body: "It stops participating in flow control and may fall behind. Resync before relying on it for reads that must be current.",
            }}
            run={() => client.apiFetch("/mariadb/cluster/desync", { method: "POST" })}
            invalidates={[["mariadb-cluster-wsrep"], ["galera-wsrep"]]}
          />
        </div>
      </Section>
      <GaleraRollingRestart />
      <CategoryOperations category="database" title="Database operations" hint="Every engine, its replication and its poolers." />

    </div>
  );
}

export function DatabaseAdmin() {
  return (
    <Tabs defaultValue="replication">
      <TabsList>
        <TabsTrigger value="replication">Replication</TabsTrigger>
        <TabsTrigger value="recovery">Recovery</TabsTrigger>
        <TabsTrigger value="databases">Databases</TabsTrigger>
        <TabsTrigger value="access">Access</TabsTrigger>
        <TabsTrigger value="control">Cluster control</TabsTrigger>
      </TabsList>
      <TabsContent value="replication">
        <ReplicationTab />
      </TabsContent>
      <TabsContent value="recovery">
        <RecoveryTab />
      </TabsContent>
      <TabsContent value="databases">
        <DatabasesTab />
      </TabsContent>
      <TabsContent value="access">
        <AccessTab />
      </TabsContent>
      <TabsContent value="control">
        <ClusterControlTab />
      </TabsContent>
    </Tabs>
  );
}
